"use client";

import Link from "next/link";
import { track } from "@vercel/analytics";
import { storyblokEditable } from "@storyblok/react/rsc";
import { getUrlWithAnchor } from "@/lib/storyblok/utils";
import { ArrowRight } from "@/components/icons/ArrowRight";

import {
  Button as ButtonComponent,
  type ButtonVariant,
} from "@/components/ui/button";
import type { CtaStoryblok } from "@/component-types-sb";

// Map Storyblok variant names to ui/button variant names
const variantMap: Record<string, ButtonVariant> = {
  primary: "black",
  secondary: "white",
  sun: "sun",
  black: "black",
  white: "white",
  link: "link",
  "mylife-green": "mylife",
  dexcom: "dexcom",
  minimed: "minimed",
};

type CtaProps = {
  blok: CtaStoryblok;
  /** `cta_location` fuer das Tracking; Article.tsx setzt `article_cta`. */
  location?: string;
};

const Cta = ({ blok, location = "content_cta" }: CtaProps) => {
  const href = getUrlWithAnchor(blok?.link) || "#";

  const mappedVariant: ButtonVariant =
    variantMap[blok.variant || ""] ?? "black";

  const isExternal = blok?.link.linktype === "url";

  const handleClick = () => {
    if (blok.tracking) {
      track(blok.tracking);
    }
  };

  // `cta_clicked` und `affiliation_link_clicked` meldet CtaClickTracker
  // ueber die data-Attribute, nicht `handleClick`: Im Artikeltext wird
  // dieser Link per renderToStaticMarkup gerendert, ein onClick laeuft
  // dort nie.
  return (
    <Link
      href={href || "#"}
      target={blok.link?.target || "_self"}
      className="cursor-pointer"
      aria-label="Mehr Informationen"
      onClick={handleClick}
      data-cta-location={location}
      data-cta-label={blok.label}
      data-cta-external={isExternal ? "" : undefined}
      {...storyblokEditable(blok)}
    >
      {blok.type === "button" ? (
        <ButtonComponent variant={mappedVariant}>
          {blok.arrow && <ArrowRight size="sm" />}
          {blok.label}
        </ButtonComponent>
      ) : (
        <span className="inline-flex gap-2 items-center underline text-button">
          <ArrowRight size="sm" />
          {blok.label}
        </span>
      )}
    </Link>
  );
};

export default Cta;
